import Helpers from '@/helpers.js'
import cfg from '@/cfg'

export default {
  store: {},
  lastType: "",
  async globalSearchSetup(result) {
    const caseType = result.caseType
    const schemaData = Helpers.prepareSearchSchema(caseType)
    const headers = Helpers.formatBucketHeaders(schemaData)
    const preferences = this.store.state.currentUser.me.preferences
    const cases = await Helpers.formatBucketData(schemaData, result.cases, preferences)
    return { caseType, headers, cases }
  },
  async getGlobalSearch(searchTerm, page, pageSize) {
    if (searchTerm && searchTerm.length > 0) {
      const payload = await this.store.dispatch("performGlobalSearch", {
        searchTerm: searchTerm,
        page: page || 0,
        pageSize: pageSize || 20
      })
      if (!payload || payload.error) {
        return {
          error: true,
          message: payload && payload.message ? payload.message : "Search did not return a valid response.",
          results: [],
          documents: { documents: [], total: 0 },
          matches: null,
          caseMatches: null,
          documentMatches: null,
          matchTotal: null,
          caseMatchTotal: null,
          documentMatchTotal: null
        }
      }
      const results = payload && payload.results ? payload.results : []
      if (results && results.length > 0) {
        results.forEach((result) => {
          const type = Helpers.getCaseTypeById(this.store.state, result.caseTypeId)
          result.caseType = type
          if (type && type.displayName) {
            result.displayName = type.displayName
          } else if (type && type.name) {
            result.displayName = type.name
          } else {
            result.displayName = "Unknown"
          }
          if (type) {
            result.iconSet = type.iconSet
            result.iconCode = type.iconCode
          }
        })
      }
      const documents = payload && payload.documents ? payload.documents : { documents: [], total: 0 }
      return {
        results: results,
        documents: documents,
        matches: payload && payload.matches ? payload.matches : null,
        caseMatches: payload && payload.caseMatches ? payload.caseMatches : null,
        documentMatches: payload && payload.documentMatches ? payload.documentMatches : null,
        matchTotal: payload ? payload.matchTotal : null,
        caseMatchTotal: payload ? payload.caseMatchTotal : null,
        documentMatchTotal: payload ? payload.documentMatchTotal : null
      }
    }
    return { results: [], documents: { documents: [], total: 0 }, matches: null }
  },
  async caseTypeSearchSetup(result) {
    if (result.caseTypeId) {
      const caseType = Helpers.getCaseTypeById(this.store.state, result.caseTypeId)
      this.lastType = caseType
      const schemaData = Helpers.prepareSearchSchema(caseType)
      const headers = Helpers.formatBucketHeaders(schemaData)
      const preferences = this.store.state.currentUser.me.preferences
      const cases = await Helpers.formatBucketData(schemaData, result.cases, preferences)
      return { caseType, headers, cases }
    }
  },
  async getCaseTypeSearch(searchTerm, caseTypeId, page, pageSize) {
    if (this.searchTerm === "") { this.searchTerm = "*" }
    const result = await this.store.dispatch("performCaseTypeSearch", { searchTerm, caseTypeId, page, pageSize })
    const caseType = Helpers.getCaseTypeById(this.store.state, caseTypeId)
    if (result && caseType) {
      result.caseTypeName = caseType.name
    }
    return result
  },
  prepareSchema(caseType) {
    const displayFields = []
    if (!caseType.displayFields.includes("title")) {
      displayFields.push("title")
    }
    displayFields.push(...caseType.displayFields.map(df => df.fieldName))
    const displayFieldsPresent = (displayFields.length > 0)

    const schemaFields = displayFieldsPresent ? displayFields : ["title", "created_at", "updated_at", "last_exported_at", "last_exported_via_bucket_id", "current_state", "owner_name", "sentiment_score", "sentiment_magnitude", "no_unread_conversations", "unresolved_task_failures"]
    const unfilteredCommonFields = [{ name: "title", type: "string" }, { name: "created_at", type: "date" }, { name: "updated_at", type: "date" }, { name: "last_exported_at", type: "date" }, { name: "last_exported_via_bucket_id", type: "number" }, { name: "current_state", type: "string" }, { name: "owner_name", type: "string" }, { name: "sentiment_score", type: "number" }, { name: "sentiment_magnitude", type: "number" }, { name: "no_unread_conversations", type: "number" }, { name: "unresolved_task_failures", type: "number" }]
    const commonFields = displayFieldsPresent ? unfilteredCommonFields.filter(sf => displayFields.includes(sf.name)) : unfilteredCommonFields

    const schema = []
    const linkedData = []
    const rightAlignData = []
    const fieldsets = [{ name: "common", fields: commonFields }].concat(caseType.currentSchema.fieldsets)
    const pickedFields = []
    if (displayFieldsPresent) {
      schemaFields.forEach(schemaField => {
        fieldsets.forEach(function(fieldset) {
          fieldset.fields.forEach(function(field) {
            if (schemaField === field.name && !pickedFields.includes(field.name)) {
              schema.push(field) // For cases
              pickedFields.push(field.name)
              if (field.type === "relation" || field.type === 'document' || schemaFields.indexOf(schemaField) === 0) { linkedData.push(field.name) }
              if (field.type === "number") { rightAlignData.push(field.name) }
            }
          })
        })
      })
    } else {
      fieldsets.forEach(function(fieldset) {
        fieldset.fields.forEach(function(field) {
          schema.push(field) // For cases
          pickedFields.push(field.name)
          if ((field.type === "relation" || field.type === 'document') || (fieldsets.indexOf(fieldset) === 0 && fieldset.fields.indexOf(field) === 0)) { linkedData.push(field.name) }
          if (field.type === "number") { rightAlignData.push(field.name) }
        })
      })
    }
    return { schema: schema, schemaFields, linkedData, rightAlignData, displayFieldsPresent }
  },
  prepareHeaders({ schemaFields, schema, linkedData, rightAlignData, displayFieldsPresent }) {
    const headers = displayFieldsPresent ? [] : []
    schema.forEach(function(header) {
      let link = false
      let right = false
      let canSort = true
      if (linkedData.includes(header.name) || header.name === 'title') { link = true }
      if (linkedData.includes(header.name) && header.name !== 'title') { canSort = false }
      if (rightAlignData.includes(header.name)) { right = true }
      if (schemaFields.includes(header.name) || !displayFieldsPresent) { // If no schema - format everything. If schema found, format specifics. Ignore others.
        const headerName = header.display_name ? header.display_name : Helpers.capitalizeRemoveUnderscores(header.name)
        headers.push({ text: headerName, value: header.name, sortable: canSort, hasLink: link, rightAlign: right })
      }
    })
    return headers
  },
  async formatData(schemaData, cases, preferences) {
    if (schemaData.schema === undefined || schemaData.schema === null) {
      console.error("Schema undefined!")
      return cases
    }
    for (const currentCase of cases) {
    // cases.forEach(function(currentCase) {
      currentCase.taskSelected = false
      currentCase.properties.updated_at = currentCase.updatedAt
      currentCase.properties.created_at = currentCase.createdAt
      currentCase.properties.last_exported_at = currentCase.lastExportedAt
      currentCase.properties.last_exported_via_bucket_id = currentCase.lastExportedViaBucketId
      currentCase.properties.no_unread_conversations = currentCase.noUnreadConversations
      currentCase.properties.unresolved_task_failures = currentCase.unresolvedTaskFailures
      currentCase.properties.sentiment_score = currentCase.sentimentScore
      currentCase.properties.sentiment_magnitude = currentCase.sentimentMagnitude
      currentCase.properties.current_state = currentCase.currentState
      for (const field of schemaData.schema) {
      // schemaData.schema.forEach(function(field) {
        if (currentCase.properties[field.name] === undefined) {
          if (field.default_value === undefined) {
            currentCase.properties[field.name] = null
          } else {
            currentCase.properties[field.name] = field.default_value
          }
        }
        switch (field.type) {
          case "boolean":
            currentCase[field.name] = currentCase.properties[field.name]
            break
          case "email_address":
          case "string":
          case "text":
          case "case_type_states":
            if (field.name === "title") {
              const titleObj = {
                value: currentCase.title,
                route: { name: 'Case', params: { case_id: currentCase.id } }
              }
              currentCase.title = titleObj
            } else {
              currentCase[field.name] = currentCase.properties[field.name]
            }
            break
          case "number":
            if (currentCase.properties[field.name] === null) { break }
            if (field.symbol !== null && field.symbol !== undefined) {
              if (field.display === "currency") { currentCase[field.name] = Helpers.formatCurrency(currentCase.properties[field.name], field.symbol) }
            } else {
              currentCase[field.name] = currentCase.properties[field.name]
            }
            break
          case "date":
            if (currentCase.properties[field.name] === null) { break }
            if (field.format !== null && field.format !== undefined) {
              currentCase[field.name] = Helpers.formatDate(currentCase.properties[field.name], field.format) // Use specified format
            } else {
              currentCase[field.name] = Helpers.formatDate(currentCase.properties[field.name], preferences.dateTimeFormat) // or use user preference.
            }
            break
          case "location":
          case "address":
            if (currentCase.properties[field.name] === null) { break }
            currentCase[field.name] = Helpers.formatAddress(currentCase.properties[field.name])
            break
          case "relation": {
            if (currentCase.properties[field.name] === null) {
              currentCase[field.name] = { value: "None", route: "" }
            } else {
              if (currentCase.properties[field.name] && currentCase.properties[field.name].id) {
                const caseId = currentCase.properties[field.name].id
                const relatedCase = await this.store.dispatch("getCaseSummaryById", { caseId })
                const relationObj = {}
                relationObj.route = { name: 'Case', params: { case_id: currentCase.properties[field.name].id } }
                if (relatedCase) {
                  let content = relatedCase.summaryVal1
                  if (relatedCase.summaryVal2) { content += " " + relatedCase.summaryVal2 }
                  if (relatedCase.summaryVal3) { content += " " + relatedCase.summaryVal3 }
                  if (relatedCase.summaryVal4) { content += " " + relatedCase.summaryVal4 }
                  if (relatedCase.summaryVal5) { content += " " + relatedCase.summaryVal5 }
                  relationObj.value = content
                } else {
                  relationObj.value = " "
                }
                currentCase[field.name] = relationObj
              }
            }
            break
          }
          case "entity":
            if (currentCase.properties[field.name] === null) { break }
            currentCase[field.name] = Helpers.formatEntity(currentCase.properties[field.name])
            break
          case "document": {
            if (currentCase.properties[field.name] === null) { currentCase.properties[field.name] = { value: "None", url: "", route: "" } }
            const docObj = {}
            if (currentCase.properties[field.name].value === "None") {
              docObj.value = "None"
              docObj.route = ""
            } else {
              docObj.value = Helpers.formatDocument(currentCase.properties[field.name])
              docObj.route = cfg.apiEndPoint() + "case_blocks" + currentCase.properties[field.name].url
            }
            currentCase[field.name] = docObj
            break
          }
          case "password":
            currentCase[field.name] = "********"
            break
          default:
            console.error("UNHANDLED TYPE! " + field.type)
        }
      }
    }
    return cases
  },
  async searchCases(searchParams) {
    const result = await this.store.dispatch('performCaseTypeSearch', { searchTerm: searchParams.searchTerm, caseTypeId: searchParams.caseTypeId, page: searchParams.page, pageSize: searchParams.itemsPerPage, sortField: searchParams.sortField, sortDirection: searchParams.sortDirection })
    return this.caseTypeSearchSetup(result)
  }
}
